import React, { useState } from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

import { AccessibleButton } from '@/pages/PreOnboarding/steps/ActivateSleepio/steps/AuthMethodSelection.styles';
import { TextField } from '@/components';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { logError } from '@/utils/logError';
import {
  PASSWORD_RULE_HINT_KEY,
  passwordErrorKey,
  passwordRuleInterpolation,
} from '@/utils/passwordPolicy';
import { HAS_PASSWORD_METADATA_KEY } from '@/utils/passwordAccount';

/**
 * "Choose a password" — the form itself, reached two ways.
 *
 * A member asked after verifying their address lands here already signed in, and a
 * member following a reset link lands here with a session the link established. Both
 * are just "there is a session, set a password on it", so this is one form with two
 * thin wrappers rather than two screens that drift apart.
 *
 * The rule is stated before the member types, not after they fail it, and both the
 * rule line and the too-short message interpolate the same constant — so the words a
 * member reads cannot disagree with the check they are about to fail.
 *
 * Errors the platform raises are shown as given. A password can satisfy every rule
 * here and still be refused for turning up in a known breach, and that refusal is
 * only meaningful if the member sees what it said.
 */

const MISMATCH_KEY = 'coverage.pre_onboarding.sleepio.password.choose.mismatch';

const schema = z.object({
  password: z.string().superRefine((value, ctx) => {
    const errorKey = passwordErrorKey(value);
    if (errorKey) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: errorKey });
    }
  }),
  confirmPassword: z.string(),
}).refine((values) => values.password === values.confirmPassword, {
  message: MISMATCH_KEY,
  path: ['confirmPassword'],
});

type FormValues = z.infer<typeof schema>;

export interface ChoosePasswordFormProps {
  /** Called once the password has actually saved. */
  onSaved: () => void;
}

const ChoosePasswordForm: React.FC<ChoosePasswordFormProps> = ({ onSaved }) => {
  const { t } = useTranslation();
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, control, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSaving(true);
    setSaveError('');
    try {
      const { error } = await getSupabaseClient().auth.updateUser({
        password: values.password,
        // Written in the same call that sets the password, because this is the only
        // moment anything knows for certain that this account now has one.
        data: { [HAS_PASSWORD_METADATA_KEY]: true },
      });

      if (error) {
        // Shown as given. A breached-password refusal is only useful if the member
        // can read what the platform actually objected to.
        logError('[ChoosePasswordForm]', 'updateUser rejected', error);
        setSaveError(error.message || t('coverage.pre_onboarding.sleepio.password.choose.error_generic'));
        setIsSaving(false);
        return;
      }
    } catch (err) {
      logError('[ChoosePasswordForm]', 'updateUser threw', err);
      setSaveError(t('coverage.pre_onboarding.sleepio.password.choose.error_generic'));
      setIsSaving(false);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="password"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            id="new-password"
            label={t('coverage.pre_onboarding.sleepio.password.choose.new_label')}
            inputProps={{
              type: 'password',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              autoComplete: 'new-password',
              'data-testid': 'new-password-input',
            }}
            error={!!fieldState.error}
            // The rule before they type; the specific failure once they have.
            helperText={fieldState.error
              ? t(fieldState.error.message ?? '', passwordRuleInterpolation)
              : t(PASSWORD_RULE_HINT_KEY, passwordRuleInterpolation)}
            FormHelperTextProps={{ 'data-testid': 'password-rule-hint' } as never}
            required
          />
        )}
      />

      <Controller
        name="confirmPassword"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            id="confirm-password"
            label={t('coverage.pre_onboarding.sleepio.password.choose.confirm_label')}
            inputProps={{
              type: 'password',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              autoComplete: 'new-password',
              'data-testid': 'confirm-password-input',
            }}
            error={!!fieldState.error}
            helperText={fieldState.error ? t(fieldState.error.message ?? '') : undefined}
            required
          />
        )}
      />

      <AccessibleButton
        variant="contained"
        data-testid="save-password-button"
        type="submit"
        fullWidth
        color="primary"
        rounded
        disabled={!formState.isValid || isSaving}
        style={{ borderRadius: 100, marginTop: 24 }}
      >
        {isSaving
          ? t('coverage.pre_onboarding.sleepio.password.choose.submitting')
          : t('coverage.pre_onboarding.sleepio.password.choose.submit')}
      </AccessibleButton>

      {saveError && (
        <Typography color="error" data-testid="save-password-error" style={{ marginTop: 8 }}>
          {saveError}
        </Typography>
      )}
    </form>
  );
};

export default ChoosePasswordForm;
